/* PuntosPendientesPanel.jsx
   Panel admin: validar puntos propuestos por choferes vía WhatsApp.
   Visible para: admin, supervisor_general_pipas, supervisor_zona, director.
   Octavio 20-jun-2026.
*/
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ESTADOS = [
  { id: "PENDIENTE", label: "Pendientes" },
  { id: "APROBADO", label: "Aprobados" },
  { id: "RECHAZADO", label: "Rechazados" },
];

const CATEGORIAS = ["PARQUE", "CAMELLON", "AREA_VERDE", "OTRO"];

export default function PuntosPendientesPanel() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [filtroEstado, setFiltroEstado] = useState("PENDIENTE");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  // Modales
  const [puntoSel, setPuntoSel] = useState(null);     // para aprobar
  const [rechazoSel, setRechazoSel] = useState(null); // para rechazar
  const [formAprobar, setFormAprobar] = useState({ nombre_final: "", categoria: "PARQUE", zona_id: "", notas: "" });
  const [motivoRechazo, setMotivoRechazo] = useState("");
  const [saving, setSaving] = useState(false);

  const cargar = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/puntos-pendientes`, { params: { estado: filtroEstado } });
      setItems(r.data.puntos || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al cargar puntos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, [filtroEstado]);

  const abrirAprobar = (p) => {
    setPuntoSel(p);
    setFormAprobar({
      nombre_final: p.nombre_propuesto || "",
      categoria: "PARQUE",
      zona_id: p.zona_id || "",
      notas: "",
    });
  };

  const confirmarAprobar = async () => {
    if (!formAprobar.nombre_final.trim()) {
      toast.error("El nombre final no puede ir vacío");
      return;
    }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/admin/puntos-pendientes/${puntoSel.id}/aprobar`, {
        nombre_final: formAprobar.nombre_final.trim(),
        categoria: formAprobar.categoria,
        zona_id: formAprobar.zona_id || null,
        notas: formAprobar.notas || null,
      });
      toast.success(`Punto aprobado: ${r.data.espacio_publico?.nombre || ""}`);
      setPuntoSel(null);
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al aprobar");
    } finally {
      setSaving(false);
    }
  };

  const confirmarRechazar = async () => {
    if (!motivoRechazo.trim()) {
      toast.error("Debes indicar el motivo");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/puntos-pendientes/${rechazoSel.id}/rechazar`, {
        motivo: motivoRechazo.trim(),
      });
      toast.success("Punto rechazado");
      setRechazoSel(null);
      setMotivoRechazo("");
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al rechazar");
    } finally {
      setSaving(false);
    }
  };

  const volver = () => {
    if (user?.rol === "director") navigate("/reporte-diario");
    else navigate("/hub");
  };

  return (
    <div style={{ minHeight: "100vh", background: "#F8FAFC", color: "#0F172A" }}>
      <header style={{
        background: "#0F172A", color: "white", padding: "12px 20px",
        display: "flex", alignItems: "center", gap: 16,
      }}>
        <i className="fa-solid fa-map-pin" style={{ fontSize: 22, color: "#C9A84C" }}/>
        <div>
          <div style={{ fontWeight: 800, fontSize: 16 }} data-testid="puntos-pendientes-titulo">
            Puntos pendientes de validar · DMU 614
          </div>
          <div style={{ fontSize: 11, color: "#94A3B8" }}>
            Propuestas de puntos nuevos enviadas por choferes vía WhatsApp
          </div>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 12, color: "#CBD5E1" }} data-testid="puntos-pendientes-usuario">
            {user?.nombre || user?.usuario} · {user?.rol}
          </span>
          <button onClick={volver} data-testid="puntos-pendientes-volver"
                  style={{ background: "#1E293B", color: "white", border: 0, padding: "6px 12px", borderRadius: 6, fontSize: 12, cursor: "pointer" }}>
            <i className="fa-solid fa-arrow-left mr-1"/>Volver
          </button>
          <button onClick={logout} data-testid="puntos-pendientes-logout"
                  style={{ background: "transparent", border: "1px solid #475569", color: "#CBD5E1", padding: "6px 12px", borderRadius: 6, fontSize: 12, cursor: "pointer" }}>
            Salir
          </button>
        </div>
      </header>

      <main style={{ padding: "20px", maxWidth: 1400, margin: "0 auto" }}>
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          {ESTADOS.map(e => (
            <button
              key={e.id}
              onClick={() => setFiltroEstado(e.id)}
              data-testid={`filtro-estado-${e.id.toLowerCase()}`}
              style={{
                padding: "8px 16px",
                borderRadius: 8,
                border: 0,
                background: filtroEstado === e.id ? "#0F172A" : "#E2E8F0",
                color: filtroEstado === e.id ? "white" : "#475569",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {e.label}
            </button>
          ))}
          <button onClick={cargar} data-testid="puntos-pendientes-refrescar"
                  style={{ marginLeft: "auto", padding: "8px 16px", background: "#0EA5E9", color: "white", border: 0, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            <i className="fa-solid fa-rotate-right mr-1"/>Refrescar
          </button>
        </div>

        {loading && <div style={{ textAlign: "center", padding: 40, color: "#64748B" }}>Cargando…</div>}

        {!loading && items.length === 0 && (
          <div style={{ textAlign: "center", padding: 60, color: "#64748B", background: "white", borderRadius: 12 }}
               data-testid="puntos-pendientes-vacio">
            <i className="fa-solid fa-inbox" style={{ fontSize: 32, marginBottom: 12, opacity: 0.5 }}/>
            <div>No hay puntos en estado {filtroEstado}.</div>
          </div>
        )}

        {!loading && items.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))", gap: 16 }}
               data-testid="puntos-pendientes-grid">
            {items.map(p => (
              <article key={p.id}
                       data-testid={`punto-card-${p.id}`}
                       style={{ background: "white", borderRadius: 12, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
                {p.foto_url ? (
                  <a href={p.foto_url} target="_blank" rel="noreferrer">
                    <img src={p.foto_url} alt={p.nombre_propuesto}
                         style={{ width: "100%", height: 180, objectFit: "cover", background: "#E2E8F0" }}/>
                  </a>
                ) : (
                  <div style={{ width: "100%", height: 180, background: "#E2E8F0",
                                display: "grid", placeItems: "center", color: "#94A3B8" }}>
                    <i className="fa-solid fa-image" style={{ fontSize: 40 }}/>
                  </div>
                )}
                <div style={{ padding: 16 }}>
                  <div style={{ fontWeight: 700, fontSize: 16, color: "#0F172A", marginBottom: 8 }}>
                    {p.nombre_propuesto || "(sin nombre)"}
                  </div>
                  <div style={{ fontSize: 12, color: "#64748B", display: "flex", flexDirection: "column", gap: 4 }}>
                    <div><i className="fa-solid fa-user fa-fw mr-2"/>{p.chofer_nombre || "?"}</div>
                    <div><i className="fa-solid fa-truck fa-fw mr-2"/>Pipa {p.pipa_id || "?"} · Zona {p.zona_id || "?"}</div>
                    <div><i className="fa-solid fa-clock fa-fw mr-2"/>{(p.creado_en || "").slice(0, 19).replace("T", " ")}</div>
                    {p.gps_lat && p.gps_lon && (
                      <div>
                        <i className="fa-solid fa-location-dot fa-fw mr-2"/>
                        <a href={`https://www.google.com/maps?q=${p.gps_lat},${p.gps_lon}`} target="_blank" rel="noreferrer"
                           style={{ color: "#0EA5E9" }}
                           data-testid={`punto-mapa-${p.id}`}>
                          {p.gps_lat.toFixed(5)}, {p.gps_lon.toFixed(5)} (ver mapa)
                        </a>
                      </div>
                    )}
                  </div>

                  {p.estado === "PENDIENTE" && (
                    <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                      <button onClick={() => abrirAprobar(p)}
                              data-testid={`btn-aprobar-${p.id}`}
                              style={{ flex: 1, background: "#16A34A", color: "white", border: 0, padding: "8px 12px", borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                        <i className="fa-solid fa-check mr-1"/>Aprobar
                      </button>
                      <button onClick={() => setRechazoSel(p)}
                              data-testid={`btn-rechazar-${p.id}`}
                              style={{ flex: 1, background: "#DC2626", color: "white", border: 0, padding: "8px 12px", borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                        <i className="fa-solid fa-xmark mr-1"/>Rechazar
                      </button>
                    </div>
                  )}

                  {p.estado === "APROBADO" && (
                    <div style={{ marginTop: 12, padding: 8, background: "#F0FDF4", borderRadius: 6, fontSize: 12, color: "#15803D" }}>
                      <div><i className="fa-solid fa-check-circle mr-1"/>Aprobado por {p.revisado_por_nombre}</div>
                      <div style={{ marginTop: 4 }}>Nombre final: <strong>{p.nombre_final}</strong> · {p.categoria}</div>
                      {p.notas_revision && <div style={{ marginTop: 4 }}>Notas: {p.notas_revision}</div>}
                    </div>
                  )}

                  {p.estado === "RECHAZADO" && (
                    <div style={{ marginTop: 12, padding: 8, background: "#FEF2F2", borderRadius: 6, fontSize: 12, color: "#991B1B" }}>
                      <div><i className="fa-solid fa-xmark-circle mr-1"/>Rechazado por {p.revisado_por_nombre}</div>
                      <div style={{ marginTop: 4 }}>Motivo: {p.motivo_rechazo}</div>
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </main>

      {/* Modal Aprobar */}
      {puntoSel && (
        <div data-testid="modal-aprobar" style={modalOverlay}>
          <div style={modalCard}>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Aprobar punto nuevo</h3>
            <div style={{ fontSize: 13, color: "#64748B", marginTop: 4 }}>
              Se creará en <strong>espacios_publicos</strong> y quedará disponible para asignar a una ruta.
            </div>

            <label style={labelStyle}>Nombre final *</label>
            <input value={formAprobar.nombre_final}
                   data-testid="aprobar-nombre"
                   onChange={(e) => setFormAprobar(f => ({ ...f, nombre_final: e.target.value }))}
                   style={inputStyle}/>

            <label style={labelStyle}>Categoría *</label>
            <select value={formAprobar.categoria}
                    data-testid="aprobar-categoria"
                    onChange={(e) => setFormAprobar(f => ({ ...f, categoria: e.target.value }))}
                    style={inputStyle}>
              {CATEGORIAS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>

            <label style={labelStyle}>Zona</label>
            <input value={formAprobar.zona_id}
                   data-testid="aprobar-zona"
                   placeholder="ej: Z1"
                   onChange={(e) => setFormAprobar(f => ({ ...f, zona_id: e.target.value }))}
                   style={inputStyle}/>

            <label style={labelStyle}>Notas (opcional)</label>
            <textarea value={formAprobar.notas}
                      data-testid="aprobar-notas"
                      onChange={(e) => setFormAprobar(f => ({ ...f, notas: e.target.value }))}
                      style={{ ...inputStyle, height: 60, resize: "vertical" }}/>

            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <button disabled={saving}
                      onClick={() => setPuntoSel(null)}
                      data-testid="aprobar-cancelar"
                      style={{ flex: 1, padding: 10, background: "#E2E8F0", border: 0, borderRadius: 6, cursor: "pointer", fontWeight: 600 }}>
                Cancelar
              </button>
              <button disabled={saving}
                      onClick={confirmarAprobar}
                      data-testid="aprobar-confirmar"
                      style={{ flex: 1, padding: 10, background: "#16A34A", color: "white", border: 0, borderRadius: 6, cursor: "pointer", fontWeight: 600 }}>
                {saving ? "Guardando…" : "Aprobar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Rechazar */}
      {rechazoSel && (
        <div data-testid="modal-rechazar" style={modalOverlay}>
          <div style={modalCard}>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Rechazar punto</h3>
            <div style={{ fontSize: 13, color: "#64748B", marginTop: 4 }}>
              Propuesto por: <strong>{rechazoSel.chofer_nombre}</strong>
            </div>

            <label style={labelStyle}>Motivo del rechazo *</label>
            <textarea value={motivoRechazo}
                      data-testid="rechazar-motivo"
                      placeholder="Ej: punto duplicado, fuera de jurisdicción, etc."
                      onChange={(e) => setMotivoRechazo(e.target.value)}
                      style={{ ...inputStyle, height: 80, resize: "vertical" }}/>

            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <button disabled={saving}
                      onClick={() => { setRechazoSel(null); setMotivoRechazo(""); }}
                      data-testid="rechazar-cancelar"
                      style={{ flex: 1, padding: 10, background: "#E2E8F0", border: 0, borderRadius: 6, cursor: "pointer", fontWeight: 600 }}>
                Cancelar
              </button>
              <button disabled={saving}
                      onClick={confirmarRechazar}
                      data-testid="rechazar-confirmar"
                      style={{ flex: 1, padding: 10, background: "#DC2626", color: "white", border: 0, borderRadius: 6, cursor: "pointer", fontWeight: 600 }}>
                {saving ? "Guardando…" : "Rechazar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const modalOverlay = {
  position: "fixed", inset: 0, background: "rgba(15,23,42,0.6)",
  display: "grid", placeItems: "center", zIndex: 50,
};
const modalCard = {
  background: "white", borderRadius: 12, padding: 24, width: "100%", maxWidth: 420,
  boxShadow: "0 20px 50px rgba(0,0,0,0.3)",
};
const labelStyle = { display: "block", fontSize: 12, fontWeight: 600, color: "#475569", marginTop: 12, marginBottom: 4 };
const inputStyle = {
  width: "100%", padding: "8px 12px", border: "1px solid #CBD5E1",
  borderRadius: 6, fontSize: 14, boxSizing: "border-box",
};
